import React, { FunctionComponent, useEffect, useState } from 'react';
import { DealerLaunchStepProps } from '../types';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { Dealer } from '../../../types/dealerTypes';
import styles from '../styles.module.scss';
import { Radio } from '../../Input/Radio/Radio';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { useFormBlock } from '../../../hooks/useFormBlock';

export enum DeskingModel {
  SELF_DESKING = 'selfDesking',
  DEFAULT_PENCIL = 'defaultPencil',
  REQUEST_PENCIL = 'requestPencil',
}

export const DealerLaunchDeskingModel: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });
  const isSelfDesking =
    form.watch('desking.enabled') &&
    form.watch('desking.defaultPencil') &&
    form.watch('desking.salespersonDesking');
  const isDefaultPencil =
    form.watch('desking.enabled') &&
    form.watch('desking.defaultPencil') &&
    form.watch('desking.managerDesking');
  const isRequestPencil =
    !isDefaultPencil && !isSelfDesking && form.watch('desking.enabled');
  let defaultModel = '';

  if (isSelfDesking) {
    defaultModel = DeskingModel.SELF_DESKING;
  } else if (isDefaultPencil) {
    defaultModel = DeskingModel.DEFAULT_PENCIL;
  } else if (isRequestPencil) {
    defaultModel = DeskingModel.REQUEST_PENCIL;
  }

  const [deskingModel, setDeskingModel] = useState<string>(defaultModel);
  // block navigation to alert user if form has any unsaved changes
  const { setBlockingStatus } = useFormBlock({
    isDirty: false,
    message:
      'This page has unsaved changes on it, are you sure you want to leave?',
  });

  useEffect(() => {
    setBlockingStatus(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deskingModel]);

  const onSubmit = async () => {
    let enabled = false;
    let defaultPencil = false;
    let salespersonDesking = false;
    let managerDesking = false;
    if (deskingModel) {
      form.setValue('desking.enabled', true);
      enabled = true;
    }

    if (deskingModel === DeskingModel.SELF_DESKING) {
      form.setValue('desking.defaultPencil', true);
      form.setValue('desking.salespersonDesking', true);
      form.setValue('desking.managerDesking', false);
      salespersonDesking = true;
      defaultPencil = true;
      managerDesking = false;
    } else if (deskingModel === DeskingModel.DEFAULT_PENCIL) {
      form.setValue('desking.defaultPencil', true);
      form.setValue('desking.managerDesking', true);
      form.setValue('desking.salespersonDesking', false);
      defaultPencil = true;
      managerDesking = true;
      salespersonDesking = false;
    } else if (deskingModel === DeskingModel.REQUEST_PENCIL) {
      form.setValue('desking.defaultPencil', false);
      form.setValue('desking.salespersonDesking', false);
      form.setValue('desking.managerDesking', false);
      defaultPencil = false;
      managerDesking = false;
      salespersonDesking = false;
    }

    const desking = dealer?.desking
      ? {
          ...dealer.desking,
          enabled,
          defaultPencil,
          salespersonDesking,
          managerDesking,
        }
      : undefined;

    const dealerUpdate: Dealer = {
      ...dealer,
      desking,
    };

    setBlockingStatus(false);
    await nextStep(dealerUpdate);
  };

  return (
    <DealerLaunchFormCardLayout
      className={styles.widgetsStep}
      form={form}
      title="Tell Us About How Your Payments Are Configured"
      subTitle=""
      cardTitle="What scenario best describes your desking model?"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <Radio
        className="flex-column"
        value={deskingModel}
        options={[
          {
            label:
              'Request a pencil: sales consultants request first pencil from a manager',
            value: DeskingModel.REQUEST_PENCIL,
          },
          {
            label: 'Self-desking: sales consultants pencil their own deals',
            value: DeskingModel.SELF_DESKING,
          },
          {
            label:
              'Default pencil: allow numbers to be shown based on default terms provided',
            value: DeskingModel.DEFAULT_PENCIL,
          },
        ]}
        onChange={(value) => {
          setDeskingModel(value);
        }}
      />
    </DealerLaunchFormCardLayout>
  );
};
